import React, { useEffect, useState } from 'react'
import { Button, Form, Input, message, Alert, Select } from 'antd'
import { CheckCircleOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useReCaptchaValidation } from '../Common/useReCaptchaValidation'
import { apiClient, ApiError } from '../../utils/apiClient'
import { GERMAN_CITIES } from '../../Constants/cities'
import './CityRequestForm.css'

const { Option } = Select

interface CityRequestFormProps {
    onSuccess?: () => void
}

export const CityRequestForm: React.FC<CityRequestFormProps> = ({
    onSuccess,
}) => {
    const { t } = useTranslation()
    const [form] = Form.useForm()
    const { validateRecaptcha } = useReCaptchaValidation()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isSuccess, setIsSuccess] = useState(false)

    useEffect(() => {
        form.resetFields()
        setIsSubmitting(false)
        setIsSuccess(false)
    }, [form])

    const handleCityChange = (cityName: string) => {
        // City name is directly selected from the German cities list
        form.setFieldsValue({ city: cityName })
    }

    const handleSubmit = async (values: { email: string; city: string }) => {
        // Validate reCAPTCHA FIRST, before any submission logic
        let recaptchaToken: string
        const recaptchaAction = 'city_expansion_submit'
        
        try {
            recaptchaToken = await validateRecaptcha(recaptchaAction)
        } catch (error) {
            form.setFields([
                {
                    name: 'recaptcha',
                    errors: [
                        error instanceof Error
                            ? error.message
                            : t('modals.cityRequestForm.securityVerificationFailed'),
                    ],
                },
            ])
            return
        }

        // City name is directly from the form value
        if (!values.city) {
            message.error(t('modals.cityRequestForm.cityInvalid'))
            return
        }

        const cityName = values.city

        // Only proceed with API submission after reCAPTCHA validation succeeds
        setIsSubmitting(true)

        try {
            await apiClient.post(
                'website/city/expansion',
                {
                    email: values.email,
                    city: cityName,
                },
                {
                    recaptchaToken,
                    recaptchaAction,
                }
            )

            // Success - show feedback and close modal after 2 seconds
            setIsSuccess(true)
            form.resetFields()
            message.success(t('modals.cityRequestForm.successDescription'))

            // Close modal after 2 seconds
            setTimeout(() => {
                if (onSuccess) {
                    onSuccess()
                }
            }, 2000)
        } catch (error) {
            console.error('Form submission error:', error)
            const errorMessage =
                error && typeof error === 'object' && 'message' in error
                    ? (error as ApiError).message
                    : t('modals.cityRequestForm.failedToSubmit')
            message.error(errorMessage)
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <Form
            form={form}
            layout="vertical"
            className="city-request-form"
            onFinish={handleSubmit}
        >
            {isSuccess ? (
                <Alert
                    message={t('modals.cityRequestForm.successTitle')}
                    description={t('modals.cityRequestForm.successDescription')}
                    type="success"
                    icon={<CheckCircleOutlined />}
                    showIcon
                    className="success-alert"
                    closable={false}
                />
            ) : (
                <>
                    <Form.Item
                        label={t('modals.cityRequestForm.emailLabel')}
                        name="email"
                        rules={[
                            {
                                required: true,
                                message: t('modals.cityRequestForm.emailRequired'),
                            },
                            {
                                type: 'email',
                                message: t('modals.cityRequestForm.emailInvalid'),
                            },
                        ]}
                        className="form-item"
                    >
                        <Input
                            placeholder={t('modals.cityRequestForm.emailPlaceholder')}
                        />
                    </Form.Item>

                    <Form.Item
                        label={t('modals.cityRequestForm.cityLabel')}
                        name="city"
                        rules={[
                            {
                                required: true,
                                message: t('modals.cityRequestForm.cityRequired'),
                            },
                        ]}
                        className="form-item"
                    >
                        <Select
                            placeholder={t('modals.cityRequestForm.cityPlaceholder')}
                            size="large"
                            showSearch
                            onChange={handleCityChange}
                            filterOption={(input, option) => {
                                const children = option?.children as string | React.ReactNode
                                if (typeof children === 'string') {
                                    return children
                                        .toLowerCase()
                                        .includes(input.toLowerCase())
                                }
                                if (children) {
                                    const text = String(children)
                                    return text.toLowerCase().includes(input.toLowerCase())
                                }
                                return false
                            }}
                            style={{ width: '100%' }}
                        >
                            {GERMAN_CITIES.map((city) => (
                                <Option key={city} value={city}>
                                    {city}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item className="submit-button-container">
                        <Button
                            type="primary"
                            htmlType="submit"
                            className="submit-button"
                            loading={isSubmitting}
                            disabled={isSubmitting}
                        >
                            {isSubmitting
                                ? t('modals.cityRequestForm.submitting')
                                : t('modals.cityRequestForm.submit')}
                        </Button>
                    </Form.Item>
                </>
            )}
        </Form>
    )
}
